import { createDisposer, globalRef, type Maybe, type Unsubscribe } from '@amazon/vinyl-util'
import type { MetricsInteractionType } from '@/metrics/uiMetrics'
import { windowEvents } from '@/util/interaction'

// GH `getInteractionType`: a uiClick reports the input that pressed its control. The window sees
// every input first, so the last one is the press a click handler is reporting.

interface Press {
    readonly event: Event
    readonly target: Maybe<Element>
}

const lastInputRef = globalRef<{ input: Maybe<MetricsInteractionType>; press: Maybe<Press> }>(
    () => ({ input: null, press: null })
)

const POINTER_INPUTS: Readonly<Record<string, MetricsInteractionType>> = {
    touch: 'TAP',
    pen: 'PRESS',
}

const CAPTURE = { capture: true, passive: true }

function record(input: MetricsInteractionType): void {
    lastInputRef.value.input = input
}

// linkedom clears `event.target` once dispatch ends, so the press keeps its own.
function recordPress(event: Event): void {
    const target = event.target
    lastInputRef.value.press = {
        event,
        target:
            target instanceof Element ? target : ((target as Maybe<Node>)?.parentElement ?? null),
    }
}

function onPointerDown(event: PointerEvent): void {
    recordPress(event)
    // Only the primary button presses a control; a right-click opens a context menu.
    if (event.button === 0) record(POINTER_INPUTS[event.pointerType] ?? 'CLICK')
}

function onKeyDown(event: KeyboardEvent): void {
    recordPress(event)
    record('KEYBOARD')
}

function onClick(event: MouseEvent): void {
    recordPress(event)
    // Enter or Space on a control clicks it with no pointer (`detail` 0).
    if (event.detail === 0) record('KEYBOARD')
    else if (lastInputRef.value.input === null || lastInputRef.value.input === 'KEYBOARD') {
        record('CLICK')
    }
}

export function captureInteractionInput(): Unsubscribe {
    const { add, dispose } = createDisposer()
    add(windowEvents.on('pointerdown', onPointerDown, CAPTURE))
    add(windowEvents.on('keydown', onKeyDown, CAPTURE))
    add(windowEvents.on('click', onClick, CAPTURE))
    add(windowEvents.on('contextmenu', recordPress, CAPTURE))
    return dispose
}

/** The input behind the press being reported; GH reads a press with no input event as a tap. */
export function inputInteraction(): MetricsInteractionType {
    return lastInputRef.value.input ?? 'TAP'
}

/** The element the customer is pressing, while that press is still being dispatched. */
export function pressedElement(): Maybe<Element> {
    const press = lastInputRef.value.press
    return press && press.event.eventPhase !== Event.NONE ? press.target : null
}
